import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Col, Row } from 'react-bootstrap';
import OnboardingHeader from './OnboardingHeader';
import OnboardingTestimonials from '../../components/Onboarding/OnboardingTestimonials';
import landingHeroBg from '../../assets/images/index-bg-3.png';
import estudioRelieveLogo from '../../assets/images/estudio-relieve.jpeg';
import cottonPrintLogo from '../../assets/images/cotton_print_chile_logo.jpeg';
import resilityLogo from '../../assets/images/resility-logo.png';
import ganaderaBrangusLogo from '../../assets/images/ganadera-brangus-spa.webp';
import ParrotLoader from '../../components/Utils/Spinner/ParrotLoader';
import { confirmOnboardingSubscriptionRequest } from '../../requests/onboardingPayment';
import {
  showOnboardingRegistrationRequest,
  finalizeOnboardingRegistrationRequest
} from '../../requests/onboardingRegistration';
import { clearOnboardingPaymentContext, loadOnboardingPaymentContext } from '../../data/platformPlans';

// Post-Transbank landing: confirms the card inscription and IMMEDIATELY runs
// the finalize (first charge + tenant) while the user watches a "Creando tu
// cuenta" loader - no manual confirm step (decision 2026-07-09). A declined
// charge surfaces the retryable payment_failed state; success/errors navigate
// to /success | /failure. The registration uuid comes in the URL
// (?registration_id=), so this page works even when Transbank's redirect lands
// in a different browser/window than the one that started the flow.
const OnboardingPaymentConfirm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // phase: confirming | creating | payment_failed
  const [phase, setPhase] = useState('confirming');
  const [errorMessage, setErrorMessage] = useState('');

  const searchParams = new URLSearchParams(location.search);
  const isIframeMode = searchParams.get('format') === 'iframe';
  const tbkToken = searchParams.get('TBK_TOKEN');
  const registrationId = searchParams.get('registration_id') || loadOnboardingPaymentContext()?.registrationId;
  // Visual test mode (no backend, no Transbank): ?preview=creating |
  // payment_failed | confirming. The retry button simulates and lands on the
  // success preview.
  const previewPhase = searchParams.get('preview');
  const landingHeroStyle = !isIframeMode ? { '--landing-hero-bg-url': `url(${landingHeroBg})` } : {};

  const goToFailure = () => {
    clearOnboardingPaymentContext();
    history.replace({ pathname: '/failure', search: isIframeMode ? '?format=iframe' : '' });
  };

  // Double-submit guard: one finalize in flight at a time (a double click on
  // "Reintentar cobro" fires both events before React re-renders the button).
  const finalizeInFlight = useRef(false);

  // First charge + tenant creation, automatic - the user only sees the loader.
  const runFinalize = (registrationData, attempt = 0) => {
    if (finalizeInFlight.current && attempt === 0) return;
    finalizeInFlight.current = true;
    setPhase('creating');
    setErrorMessage('');

    if (previewPhase) {
      setTimeout(() => {
        history.replace({
          pathname: '/success',
          search: `?account=test&name=${registrationData?.firstname || 'John'}&plan=${registrationData?.platform_plan ||
            'pro'}&email=${registrationData?.email || ''}`
        });
      }, 2500);
      return;
    }

    finalizeOnboardingRegistrationRequest(registrationId, {
      dispatch,
      successCallback: response => {
        const { result, status, error } = response.data || {};
        if (result === true) {
          clearOnboardingPaymentContext();
          history.replace({
            pathname: '/success',
            search: isIframeMode ? '?format=iframe' : '',
            state: {
              firstname: registrationData?.firstname,
              planId: registrationData?.platform_plan,
              accountName: registrationData?.account_domain,
              email: registrationData?.email
            }
          });
          return;
        }
        if (status === 'processing') {
          // Another finalize holds the claim (duplicated tab / prior timeout):
          // keep the loader and re-check shortly - the winning request will
          // have resolved to completed or payment_failed by then.
          if (attempt < 5) {
            setTimeout(() => runFinalize(registrationData, attempt + 1), 4000);
          } else {
            goToFailure();
          }
          return;
        }
        finalizeInFlight.current = false;
        if (status === 'payment_failed') {
          setErrorMessage(error || 'El cobro fue rechazado por el emisor de tu tarjeta.');
          setPhase('payment_failed');
          return;
        }
        goToFailure();
      },
      failureCallback: () => goToFailure()
    });
  };

  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    if (previewPhase) {
      const fakeRegistration = {
        firstname: searchParams.get('name') || 'John',
        platform_plan: searchParams.get('plan') || 'pro',
        account_domain: 'test',
        email: 'test@empresa.cl'
      };
      setRegistration(fakeRegistration);
      if (previewPhase === 'payment_failed') {
        setErrorMessage('Tarjeta rechazada por el emisor. (modo prueba)');
        setPhase('payment_failed');
      } else if (previewPhase === 'confirming') {
        setPhase('confirming');
      } else {
        setPhase('creating');
      }
      return;
    }

    if (!registrationId) {
      goToFailure();
      return;
    }

    if (tbkToken) {
      confirmOnboardingSubscriptionRequest({
        dispatch,
        params: { registration_uuid: registrationId, token: tbkToken },
        successCallback: response => {
          const registrationData = response.data?.registration;
          setRegistration(registrationData);
          runFinalize(registrationData);
        },
        failureCallback: () => goToFailure()
      });
      return;
    }

    // Rehydration without a fresh token (e.g. reopening the link after a
    // declined charge): the registration must already have a card.
    showOnboardingRegistrationRequest(registrationId, {
      dispatch,
      successCallback: response => {
        const { data } = response;
        if (!data?.card_registered) {
          goToFailure();
          return;
        }
        setRegistration(data);
        if (data.status === 'payment_failed') {
          setErrorMessage(data.last_error || 'El cobro fue rechazado. Puedes reintentar.');
          setPhase('payment_failed');
        } else {
          runFinalize(data);
        }
      },
      failureCallback: () => goToFailure()
    });
    // eslint-disable-next-line
  }, []);

  const retryContent = (
    <div className="onboarding-card__form mt-3">
      <div className="pf-alert pf-alert--error mb-3">
        <span>{errorMessage} Tu tarjeta sigue inscrita: puedes reintentar el cobro sin volver a ingresarla.</span>
      </div>

      <div className="onboarding-card__form-actions">
        <Button className="btn pf-btn pf-btn--gradient pf-btn--block" onClick={() => runFinalize(registration)}>
          Reintentar cobro
        </Button>
      </div>
    </div>
  );

  const loaderTitle = phase === 'confirming' ? 'Confirmando tu tarjeta...' : 'Creando tu cuenta... ¡Ya no falta nada!';

  const statusCard = (
    <div className="onboarding-card-stack d-flex flex-column mt-2">
      {!isIframeMode && (
        <div className="onboarding-card-stack__logo mb-1">
          <OnboardingHeader isIframeMode={isIframeMode} />
        </div>
      )}

      <div className={isIframeMode ? 'iframe-form' : 'onboarding-card'}>
        {!isIframeMode && phase === 'payment_failed' && (
          <div className="d-none d-md-block onboarding-card__intro">
            <h1 className="pf-card__title mb-1">No pudimos procesar el cobro</h1>
          </div>
        )}

        {phase === 'payment_failed' ? (
          retryContent
        ) : (
          <div className="d-flex justify-content-center align-items-center py-5">
            <ParrotLoader title={loaderTitle} />
          </div>
        )}

        {!isIframeMode && (
          <>
            <div className="pf-divider" />
            <div className="onboarding-left__trusted text-center">
              <p className="pf-trust__title mb-2">+500 empresas ya confían en Parrotfy</p>
              <div className="onboarding-left__logos d-flex justify-content-center align-items-center flex-wrap mb-2">
                <img src={estudioRelieveLogo} alt="Estudio Relieve" />
                <img src={cottonPrintLogo} alt="Cotton Print Chile" />
                <img src={resilityLogo} alt="Resility" />
                <img src={ganaderaBrangusLogo} alt="Ganadera Brangus" />
              </div>
            </div>
          </>
        )}
      </div>

      {!isIframeMode && (
        <div className="d-md-none onboarding-mobile-testimonials mt-4">
          <OnboardingTestimonials />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={isIframeMode ? 'onboarding-page onboarding-page--iframe' : 'onboarding-page landing-hero'}
      style={!isIframeMode ? landingHeroStyle : undefined}
    >
      {!isIframeMode && (
        <>
          <div className="landing-hero__bg" />
          <div className="landing-hero__wash" />
          <div className="landing-hero__darken" />
          <div className="landing-hero__tint" />
        </>
      )}
      <div
        className={
          isIframeMode
            ? 'onboarding-page__content d-flex align-items-center justify-content-between'
            : 'landing-hero__content d-flex align-items-center justify-content-between'
        }
      >
        {isIframeMode ? (
          <div className="iframe-column w-100">{statusCard}</div>
        ) : (
          <Row className="justify-content-center align-items-start" noGutters>
            <Col xs={12} md={4} className="pl-md-2 pr-2 pl-2 col-md-4 col-12">
              {statusCard}
            </Col>
            <Col
              xs={12}
              md={8}
              className="onboarding-right-col d-none d-md-flex align-items-stretch justify-content-center"
            >
              <div className="onboarding-right d-flex flex-column">
                <div className="onboarding-right__intro text-center">
                  <h2 className="users-title font-weight-bold mb-1">Haz que tu negocio no dependa de ti</h2>
                  <p className="m-0">
                    Conecta SII, bancos y previred para empezar a delegar las finanzas de tu negocio
                  </p>
                </div>
                <OnboardingTestimonials />
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default OnboardingPaymentConfirm;
